import * as React from 'react';
import { Badge, Button, makeStyles, shorthands, Text, tokens, Tooltip } from '@fluentui/react-components';
import { Add20Regular, ArrowDown20Regular, ArrowUp20Regular, Delete20Regular, QuestionCircle24Regular } from '@fluentui/react-icons';
import { createEmptyQuestion, IEditableQuestion } from '../../lib/pollQuestions';
import { getQuestionTypeLabel } from '../../lib/pollLabels';
import { PollQuestionType } from '../../services/DataService';
import QuestionEditor from './QuestionEditor';
import EmptyState from '../ui/EmptyState';

const useStyles = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
    },
    card: {
        display: 'flex',
        flexDirection: 'column',
        gap: '14px',
        padding: '16px',
        border: `1px solid ${tokens.colorNeutralStroke2}`,
        borderRadius: tokens.borderRadiusLarge,
        backgroundColor: tokens.colorNeutralBackground1,
        '@media (max-width: 767px)': {
            padding: '12px',
        },
    },
    cardError: {
        ...shorthands.borderColor(tokens.colorStatusDangerBorder1),
    },
    cardHead: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '8px',
    },
    cardTitle: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        minWidth: 0,
    },
    number: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '26px',
        height: '26px',
        borderRadius: tokens.borderRadiusCircular,
        backgroundColor: tokens.colorBrandBackground2,
        color: tokens.colorBrandForeground1,
        fontSize: tokens.fontSizeBase200,
        fontWeight: tokens.fontWeightSemibold,
        flexShrink: 0,
    },
    cardActions: {
        display: 'flex',
        gap: '2px',
        flexShrink: 0,
    },
    footer: {
        display: 'flex',
        justifyContent: 'flex-start',
    },
});

export interface IQuestionsEditorProps {
    questions: IEditableQuestion[];
    onChange: (questions: IEditableQuestion[]) => void;
    errors?: Record<string, string>;
    showErrors?: boolean;
    listError?: string;
}

/** Ordered list of question editors with add / remove / reorder. */
const QuestionsEditor: React.FC<IQuestionsEditorProps> = ({ questions, onChange, errors, showErrors, listError }) => {
    const styles = useStyles();
    const [lastAddedKey, setLastAddedKey] = React.useState<string>();

    const update = (index: number, question: IEditableQuestion): void => {
        onChange(questions.map((q, i) => (i === index ? question : q)));
    };

    const add = (): void => {
        const question = createEmptyQuestion(PollQuestionType.Rating);
        setLastAddedKey(question.key);
        onChange([...questions, question]);
    };

    const remove = (index: number): void => onChange(questions.filter((_, i) => i !== index));

    const move = (index: number, delta: number): void => {
        const target = index + delta;
        if (target < 0 || target >= questions.length) return;
        const next = questions.slice();
        [next[index], next[target]] = [next[target], next[index]];
        onChange(next);
    };

    return (
        <div className={styles.root}>
            {questions.length === 0 && (
                <EmptyState
                    icon={<QuestionCircle24Regular />}
                    title={listError && showErrors ? listError : 'Вопросов пока нет'}
                    description="Добавьте первый вопрос вручную или выберите готовый шаблон."
                    action={{ label: 'Добавить вопрос', onClick: add, icon: <Add20Regular /> }}
                />
            )}

            {questions.map((question, index) => {
                const error = errors?.[question.key];
                return (
                    <div
                        key={question.key}
                        className={`${styles.card} ${showErrors && error ? styles.cardError : ''}`}
                        aria-label={`Вопрос ${index + 1}`}
                    >
                        <div className={styles.cardHead}>
                            <div className={styles.cardTitle}>
                                <span className={styles.number} aria-hidden>{index + 1}</span>
                                <Text weight="semibold">Вопрос {index + 1}</Text>
                                <Badge appearance="tint" color="brand" size="small" shape="rounded">
                                    {getQuestionTypeLabel(question.Type)}
                                </Badge>
                            </div>
                            <div className={styles.cardActions}>
                                <Tooltip content="Выше" relationship="label" withArrow>
                                    <Button appearance="subtle" size="small" icon={<ArrowUp20Regular />} disabled={index === 0} onClick={() => move(index, -1)} />
                                </Tooltip>
                                <Tooltip content="Ниже" relationship="label" withArrow>
                                    <Button appearance="subtle" size="small" icon={<ArrowDown20Regular />} disabled={index === questions.length - 1} onClick={() => move(index, 1)} />
                                </Tooltip>
                                <Tooltip content="Удалить вопрос" relationship="label" withArrow>
                                    <Button appearance="subtle" size="small" icon={<Delete20Regular />} onClick={() => remove(index)} />
                                </Tooltip>
                            </div>
                        </div>
                        <QuestionEditor
                            question={question}
                            onChange={(q) => update(index, q)}
                            error={error}
                            showErrors={showErrors}
                            autoFocus={question.key === lastAddedKey}
                        />
                    </div>
                );
            })}

            {questions.length > 0 && (
                <div className={styles.footer}>
                    <Button appearance="secondary" icon={<Add20Regular />} onClick={add}>
                        Добавить вопрос
                    </Button>
                </div>
            )}
        </div>
    );
};

export default QuestionsEditor;
